import type { AdminTelemetryDailyPoint } from "../../api/types";

/**
 * issue #355: the backend deliberately ships one flat row per day×tipo,
 * never a date×type matrix ("el frontend pivotea si hace falta, el backend
 * no arma una matriz"). Kept in its own module (same convention as
 * `campaignSort.ts` next to it) so it's unit-testable in isolation from the
 * screen's rendering/query wiring.
 */
export interface DailySeriesPivot {
  /** Every distinct `fecha` in the window, ascending — including days with
   * zero events of every type (see `fechas` derivation below), so a quiet
   * day never silently disappears from the table. */
  fechas: string[];
  /** Every distinct `tipo` actually present in `points`, in a stable order
   * (first-seen). The backend only ever emits available (measured) tipos —
   * never a pending/blocked one — so this naturally excludes those. */
  tipos: string[];
  /** `matrix[fecha][tipo]` — absent combinations mean a confirmed zero (the
   * window is complete by construction), never "unknown"; callers default
   * to 0 when reading a missing key. */
  matrix: Record<string, Record<string, number>>;
}

/**
 * `windowStart`/`days` (not just `points`) are required so a day with ZERO
 * events of every type still gets its own row — `points` alone would
 * silently skip it, making a quiet day indistinguishable from "outside the
 * window" instead of "confirmed zero activity".
 */
export function pivotDailySeries(points: readonly AdminTelemetryDailyPoint[], windowStart: string, days: number): DailySeriesPivot {
  const tipos: string[] = [];
  const seenTipos = new Set<string>();
  const matrix: Record<string, Record<string, number>> = {};

  const fechas: string[] = [];
  const [year, month, day] = windowStart.split("-").map(Number);
  const cursor = new Date(Date.UTC(year!, (month ?? 1) - 1, day ?? 1));
  for (let i = 0; i < days; i++) {
    fechas.push(cursor.toISOString().slice(0, 10));
    matrix[fechas[i]!] = {};
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  for (const point of points) {
    if (!seenTipos.has(point.tipo)) {
      seenTipos.add(point.tipo);
      tipos.push(point.tipo);
    }
    const row = matrix[point.fecha];
    if (row) {
      row[point.tipo] = point.conteo;
    }
  }

  return { fechas, tipos, matrix };
}
